/**
 * Earth Therapy — filtering, sorting and load more.
 *
 * Requested by sections/main-collection.liquid and
 * sections/main-search.liquid, which share one implementation.
 */
(function () {
  'use strict';

  var core = window.Theme && window.Theme.core;
  if (!core) return;

  var define = core.define;
  var announce = core.announce;
  var revealWithin = core.revealWithin;
  var fadeImagesWithin = core.fadeImagesWithin;

  /* ------------------------------------------------------------ <et-facets> */

  /**
   * Collection filtering and sorting without a page reload.
   *
   * Progressive enhancement: the markup is a real <form method="get">, so with
   * JavaScript off every filter and the sort menu still work by submitting
   * normally. This only intercepts to swap the results in place and keep the
   * URL in step, so back/forward and sharing a filtered URL both behave.
   */
  class EtFacets extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-facets-form]');
      this.sectionId = this.getAttribute('data-section-id');
      if (!this.form || !this.sectionId) return;

      this.controller = null;

      // Listen on the element rather than the form: the mobile drawer sits
      // outside the form in the DOM and is associated to it with form="…",
      // so its change events bubble here but not through the form.
      this.addEventListener('change', this.onChange.bind(this));
      this.form.addEventListener('submit', function (event) {
        event.preventDefault();
      });

      // Clear links and active-filter chips are plain <a> so they work
      // without JS; intercept them here to keep the page in place.
      this.addEventListener('click', this.onClick.bind(this));

      this.onPopState = this.onPopState.bind(this);
      window.addEventListener('popstate', this.onPopState);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
    }

    onClick(event) {
      var link = event.target.closest('[data-facet-link]');
      if (!link) return;
      event.preventDefault();
      this.load(link.getAttribute('href'), true);
    }

    onChange() {
      var params = new URLSearchParams(new FormData(this.form));

      // Drop empty values so the URL stays readable and shareable.
      var cleaned = new URLSearchParams();
      params.forEach(function (value, key) {
        if (value !== '') cleaned.append(key, value);
      });

      var query = cleaned.toString();
      this.load(window.location.pathname + (query ? '?' + query : ''), true);
    }

    onPopState() {
      this.load(window.location.href, false);
    }

    load(url, push) {
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      this.setBusy(true);

      var fetchUrl = new URL(url, window.location.origin);
      fetchUrl.searchParams.set('section_id', this.sectionId);

      fetch(fetchUrl.toString(), { signal: this.controller.signal })
        .then(function (res) {
          if (!res.ok) throw new Error('filter request failed');
          return res.text();
        })
        .then(
          function (html) {
            var parsed = new DOMParser().parseFromString(html, 'text/html');

            ['[data-facets-panel]', '[data-product-grid]', '[data-result-count]', '[data-active-facets]'].forEach(
              function (selector) {
                var incoming = parsed.querySelector(selector);
                var current = this.querySelector(selector);
                if (incoming && current) current.innerHTML = incoming.innerHTML;
              }.bind(this)
            );

            if (push) {
              var clean = new URL(url, window.location.origin);
              clean.searchParams.delete('section_id');
              window.history.pushState({}, '', clean.toString());
            }

            revealWithin(this);
            fadeImagesWithin(this);

            var count = this.querySelector('[data-result-count]');
            if (count) announce(count.textContent.trim());
          }.bind(this)
        )
        .catch(
          function (error) {
            if (error.name === 'AbortError') return;
            // Fall back to a normal navigation rather than leaving the
            // customer on a stale grid.
            window.location.href = url;
          }
        )
        .finally(
          function () {
            this.setBusy(false);
          }.bind(this)
        );
    }

    setBusy(state) {
      var grid = this.querySelector('[data-product-grid]');
      if (grid) grid.classList.toggle('is-loading', state);
      this.setAttribute('aria-busy', state ? 'true' : 'false');
    }
  }

  define('et-facets', EtFacets);

  /* --------------------------------------------------------- <et-load-more> */

  /**
   * Appends the next page in place. The button is a real link to page 2, so
   * with JavaScript off it is ordinary pagination.
   */
  class EtLoadMore extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-load-more]');
      this.sectionId = this.getAttribute('data-section-id');
      if (!this.button || !this.sectionId) return;

      this.button.addEventListener('click', this.onClick.bind(this));
    }

    onClick(event) {
      event.preventDefault();

      var next = this.button.getAttribute('href');
      if (!next) return;

      var url = new URL(next, window.location.origin);
      url.searchParams.set('section_id', this.sectionId);

      this.button.setAttribute('aria-busy', 'true');
      this.button.setAttribute('aria-disabled', 'true');

      fetch(url.toString())
        .then(function (res) {
          if (!res.ok) throw new Error('load more failed');
          return res.text();
        })
        .then(
          function (html) {
            var parsed = new DOMParser().parseFromString(html, 'text/html');

            var incomingGrid = parsed.querySelector('[data-product-grid]');
            var grid = document.querySelector('[data-product-grid]');
            if (incomingGrid && grid) {
              var added = [];
              Array.prototype.forEach.call(incomingGrid.children, function (child) {
                added.push(child.cloneNode(true));
              });
              added.forEach(function (node) {
                grid.appendChild(node);
              });
              revealWithin(grid);
              fadeImagesWithin(grid);

              // Move focus to the first new card so keyboard users are not
              // dropped back at the top of the list.
              if (added.length) {
                var focusTarget = added[0].querySelector('a');
                if (focusTarget) {
                  focusTarget.setAttribute('tabindex', '-1');
                  focusTarget.focus({ preventScroll: true });
                }
                announce(added.length + ' more products loaded');
              }
            }

            // Swap in the next button, or remove it on the last page.
            var incomingMore = parsed.querySelector('[data-load-more]');
            if (incomingMore) {
              this.button.setAttribute('href', incomingMore.getAttribute('href'));
            } else {
              this.remove();
            }
          }.bind(this)
        )
        .catch(
          function () {
            window.location.href = next;
          }
        )
        .finally(
          function () {
            if (!this.button) return;
            this.button.removeAttribute('aria-busy');
            this.button.removeAttribute('aria-disabled');
          }.bind(this)
        );
    }
  }

  define('et-load-more', EtLoadMore);
})();
